import { FC, ReactNode, useEffect, useState } from 'react'

import { usePrevious } from '@sequencehq/utils'
import {
  IconRocket,
  EmptyState,
  GreyGrey30,
  GreyGrey70,
  Spinner,
  StandardLayout,
  Center,
  Box,
  Flex
} from '@sequencehq/core-components'

import { ButtonNewWorkflow } from '../../../components/ButtonNewWorkflow'

const InboxListContainer: FC<{
  headerActions?: ReactNode
  filterRow?: ReactNode
  children: ReactNode
  isPending: boolean
  'data-testid'?: string
}> = ({
  headerActions,
  filterRow,
  children,
  isPending,
  'data-testid': dataTestId
}) => {
  /**
   * To prevent too much UI churn, we only fade in the loading state after a small
   * delay.
   */
  const [showLoadingComponent, setShowLoadingComponent] = useState(false)
  const prevIsPending = usePrevious(isPending)

  useEffect(() => {
    if (isPending && !prevIsPending) {
      setTimeout(() => setShowLoadingComponent(true), 250)
    }
    if (!isPending) {
      setShowLoadingComponent(false)
    }
  }, [isPending, prevIsPending])

  return (
    <Wrapper
      headerActions={headerActions}
      filterRow={filterRow}
      data-testid={dataTestId}
    >
      {isPending ? (
        <Center
          width="100%"
          opacity={showLoadingComponent ? 1 : 0}
          transition="opacity 250ms"
          height="100%"
        >
          <Spinner color={GreyGrey70} />
        </Center>
      ) : (
        <Box
          flex="1"
          minHeight="0"
          flexDirection="column"
          padding="8px"
          overflowY="auto"
        >
          {children}
        </Box>
      )}
    </Wrapper>
  )
}

const Wrapper: FC<{
  headerActions?: ReactNode
  filterRow?: ReactNode
  children: ReactNode
  'data-testid'?: string
}> = ({ headerActions, filterRow, children, 'data-testid': dataTestId }) => {
  return (
    <Flex
      flexDir="column"
      height="100%"
      borderRight={`1px solid ${GreyGrey30}`}
      width="400px"
      data-testid={dataTestId}
    >
      <StandardLayout.Header title="Workflow history" actions={headerActions} />
      {filterRow}
      {children}
    </Flex>
  )
}

const InboxEmptyState: FC = () => {
  return (
    <EmptyState
      icon={IconRocket}
      title="No workflow history"
      body="Information about your workflow activity will appear here"
      button={<ButtonNewWorkflow />}
      data-testid="automations.management.executionsHistory.emptyState"
    />
  )
}

export const InboxList = Object.assign(InboxListContainer, {
  EmptyState: InboxEmptyState
})
